/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [APP-LINK] THIS BROWSER'S DEVICE ID — WHAT THE API'S PER-DEVICE METER COUNTS.
 *
 *  `POST /auth/request-app-link` allows each device a fixed number of requests an hour. A request
 *  that names no device is counted by its network address instead, and every officer at one
 *  police station shares one address — so the page sends an id whenever this browser can make
 *  one, and twenty officers behind one router are twenty devices rather than one.
 *
 *  ⚠️ IT IS A FAIRNESS CONVENIENCE, NEVER A CONTROL. The client chooses it and can change it on
 *  every request; the server's per-address and per-number meters are the ones a caller cannot
 *  shed (`APP_LINK_DEVICE_ID_PATTERN`'s header in `@pams/shared` says the same).
 *
 *  ⚠️ 16 BYTES FROM `crypto.getRandomValues`, NEVER `crypto.randomUUID` — divergence #28.
 *  `randomUUID` is secure-context-only, so it is `undefined` over plain `http://` to a LAN
 *  address; Node and jsdom expose it unconditionally, so no test sees that without a stub. 16
 *  bytes so that nobody can guess another device's id and spend its hour.
 *
 *  ⚠️ AND NO `Math.random` FALLBACK. With no CSPRNG this answers `undefined` and the page sends no
 *  id, so the request is counted by address — exactly like a client that never sends one. Fail
 *  closed: `client-core/src/auth/rotationKey.ts`'s rule.
 *
 *  ⚠️ IT LIVES IN `pages/`, NOT `lib/`. The `src/lib/**` lint block bans storage writes, and
 *  `lib/eslintConfig.test.ts` E4 refuses even a read there, because that rule is about
 *  credentials. This is not one.
 *
 *  ⚠️ NOT CLEARED ON SIGN-OUT. It names the browser, not an officer, and `AuthProvider` sweeps only
 *  the prefixes it owns.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** A `pams.` key, outside every prefix `AuthProvider` sweeps. */
const STORAGE_KEY = 'pams.download-app.device';

/**
 * Exactly what `mint` produces. That always satisfies the server's wider `APP_LINK_DEVICE_ID_PATTERN`
 * band. A stored value of any other shape was not written by this file, so it is replaced rather
 * than sent — one the server refused would turn every request from this browser into a 422.
 */
const DEVICE_ID = /^[0-9a-f]{32}$/;

const HEX = '0123456789abcdef';

/**
 * ⚠️ A THUNK, CALLED INSIDE `try`. In a sandboxed frame the property access ITSELF throws, so
 * naming the global outside the `try` fails in exactly the case the `try` is written for
 * (`useArrangementListFilters.ts` records the same rule).
 */
const area = (): Storage => window.localStorage;

/**
 * The id this page load last answered with. When storage throws, or silently drops the write, this
 * is what keeps the id stable until a reload — so an unusable store costs persistence across loads,
 * never a fresh id on every request.
 */
let memo: string | undefined;

const mint = (): string | undefined => {
  const source = globalThis.crypto;
  if (!source?.getRandomValues) return undefined;
  const bytes = source.getRandomValues(new Uint8Array(16));
  let id = '';
  for (const byte of bytes) id += `${HEX[byte >> 4] ?? '0'}${HEX[byte & 15] ?? '0'}`;
  return id;
};

const readStored = (): string | undefined => {
  try {
    const stored = area().getItem(STORAGE_KEY);
    return stored !== null && DEVICE_ID.test(stored) ? stored : undefined;
  } catch {
    /* Unreadable storage holds no id this page can use; the memo carries one instead. */
    return undefined;
  }
};

/**
 * Writes, then READS BACK — `useArrangementListFilters`' `writeRaw` rule: only a value that can be
 * read back counts as stored. It answers with the id the store now holds, which is the id the next
 * call will read, and with the id it was given when the write threw or was dropped.
 */
const persist = (id: string): string => {
  try {
    const storage = area();
    storage.setItem(STORAGE_KEY, id);
    const landed = storage.getItem(STORAGE_KEY);
    if (landed !== null && DEVICE_ID.test(landed)) return landed;
  } catch {
    /* Unwritable storage: the memo carries the id for this page load. */
  }
  return id;
};

/**
 * The id to send with an app-link request, or `undefined` when this browser cannot mint one.
 *
 * ⚠️ CALLED WHEN A REQUEST IS SENT, NEVER AT RENDER — a page that is only looked at writes nothing.
 */
export const appLinkDeviceId = (): string | undefined => {
  const stored = readStored();
  if (stored !== undefined) {
    memo = stored;
    return stored;
  }
  const id = memo ?? mint();
  if (id === undefined) return undefined;
  memo = persist(id);
  return memo;
};
